import { AxiosResponse, isAxiosError } from 'axios';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { httpDELETE, httpGET, httpPOST } from '../../services/http.ts';
import { openExternalPage } from '../../services/navigation.ts';
import {
  BillingCatalog,
  BillingRedirect,
  PageResponse,
  Subscription,
  SubscriptionListItem,
  SubscriptionListQuery,
  buildSubscriptionListUrl,
} from '../../types/subscription.ts';
import { subscriptionReducer } from '../reducers/subscription.reducer.ts';
import { toastReducer } from '../reducers/toast.reducer.ts';
import { userReducer } from '../reducers/user.reducer.ts';
import { getAllSubscriptionsQuery } from '../selectors/subscription.selector.ts';

const {
  loadSubscription,
  setSubscription,
  setSubscriptionError,
  loadAllActiveSubscriptions,
  setAllActiveSubscriptions,
  assignSubscription,
  setAssignSubscriptionSuccess,
  cancelSubscription,
  setCancelSubscriptionSuccess,
  loadBillingCatalog,
  setBillingCatalog,
  startSubscriptionCheckout,
  startCreditPackCheckout,
  openBillingPortal,
  setOpeningPaymentPageFailed,
  switchToFree,
  setSwitchToFreeFailed,
} = subscriptionReducer.actions;
const { refreshUser } = userReducer.actions;

const { showToast } = toastReducer.actions;

function* loadSubscriptionAction() {
  try {
    const { data }: AxiosResponse<Subscription> = yield call(httpGET, `/subscriptions/user`);
    yield put(setSubscription(data));
  } catch (error) {
    let errorMessage = 'settings.plans.errorLoadingSubscription';
    if (isAxiosError(error) && error.response?.data) {
      errorMessage = error.response.data;
    }
    yield put(setSubscriptionError(errorMessage));
  }
}

function* loadAllActiveSubscriptionsAction(action: ReturnType<typeof loadAllActiveSubscriptions>) {
  try {
    const { data }: AxiosResponse<PageResponse<SubscriptionListItem>> = yield call(
      httpGET,
      buildSubscriptionListUrl(action.payload),
    );
    yield put(setAllActiveSubscriptions(data));
  } catch (error) {
    let errorMessage = 'Error loading all active subscriptions';
    if (isAxiosError(error) && error.response?.data) {
      errorMessage = error.response.data;
    }
    yield put(setSubscriptionError(errorMessage));
  }
}

function* reloadAllActiveSubscriptions() {
  const query: SubscriptionListQuery = yield select(getAllSubscriptionsQuery);
  yield put(loadAllActiveSubscriptions(query));
}

function* assignSubscriptionAction(action: ReturnType<typeof assignSubscription>) {
  try {
    const { userId, request } = action.payload;
    yield call(httpPOST, `/subscriptions/${userId}`, request);
    yield put(setAssignSubscriptionSuccess());
    yield* reloadAllActiveSubscriptions();
  } catch (error) {
    let errorMessage = 'Error assigning subscription';
    if (isAxiosError(error) && error.response?.data) {
      errorMessage = error.response.data;
    }
    yield put(setSubscriptionError(errorMessage));
  }
}

function* cancelSubscriptionAction(action: ReturnType<typeof cancelSubscription>) {
  try {
    const userId = action.payload;
    yield call(httpDELETE, `/subscriptions/${userId}`, {});
    yield put(setCancelSubscriptionSuccess());
    yield* reloadAllActiveSubscriptions();
  } catch (error) {
    let errorMessage = 'Error cancelling subscription';
    if (isAxiosError(error) && error.response?.data) {
      errorMessage = error.response.data;
    }
    yield put(setSubscriptionError(errorMessage));
  }
}

export function* loadBillingCatalogAction() {
  try {
    const { data }: AxiosResponse<BillingCatalog> = yield call(httpGET, `/billing/catalog`);
    yield put(setBillingCatalog(data));
  } catch (_error) {
    // Without a catalog the plans are shown without prices and cannot be bought.
    yield put(setBillingCatalog(undefined));
  }
}

/** Ask the backend for a page at the payment provider and send the browser there. */
function* openPaymentPage(path: string, body: object) {
  try {
    const { data }: AxiosResponse<BillingRedirect> = yield call(httpPOST, path, body);
    // isOpeningPaymentPage stays set: the app is being left.
    yield call(openExternalPage, data.url);
  } catch (error) {
    let errorMessage = 'settings.billing.errorOpeningPaymentPage';
    if (isAxiosError(error) && typeof error.response?.data === 'string' && error.response.data) {
      errorMessage = error.response.data;
    }
    yield put(setOpeningPaymentPageFailed());
    yield put(showToast({ message: errorMessage, type: 'error' }));
  }
}

export function* startSubscriptionCheckoutAction(action: ReturnType<typeof startSubscriptionCheckout>) {
  yield call(openPaymentPage, `/billing/checkout/subscription`, { tier: action.payload });
}

export function* startCreditPackCheckoutAction(action: ReturnType<typeof startCreditPackCheckout>) {
  yield call(openPaymentPage, `/billing/checkout/credits`, { packKey: action.payload });
}

export function* openBillingPortalAction() {
  yield call(openPaymentPage, `/billing/portal`, {});
}

export function* switchToFreeAction() {
  try {
    const { data }: AxiosResponse<Subscription> = yield call(httpPOST, `/billing/switch-to-free`, {});
    yield put(setSubscription(data));
    // The plan's credits go with it.
    yield put(refreshUser());
    yield put(showToast({ message: 'settings.plans.switchedToFree', type: 'success' }));
  } catch (error) {
    let errorMessage = 'settings.plans.errorSwitchingToFree';
    if (isAxiosError(error) && typeof error.response?.data === 'string' && error.response.data) {
      errorMessage = error.response.data;
    }
    yield put(setSwitchToFreeFailed());
    yield put(showToast({ message: errorMessage, type: 'error' }));
  }
}

export default function* subscriptionSaga() {
  yield all([
    takeLatest(loadSubscription, loadSubscriptionAction),
    takeLatest(loadAllActiveSubscriptions, loadAllActiveSubscriptionsAction),
    takeLatest(assignSubscription, assignSubscriptionAction),
    takeLatest(cancelSubscription, cancelSubscriptionAction),
    takeLatest(loadBillingCatalog, loadBillingCatalogAction),
    takeLatest(startSubscriptionCheckout, startSubscriptionCheckoutAction),
    takeLatest(startCreditPackCheckout, startCreditPackCheckoutAction),
    takeLatest(openBillingPortal, openBillingPortalAction),
    takeLatest(switchToFree, switchToFreeAction),
  ]);
}
